import { useEffect, useRef, useState } from "react";
import { SignIn } from "@clerk/react";
import { qrSvgDataUrl } from "@workspace/player-shell/signage";
import { Loader2, Smartphone } from "lucide-react";
import {
  useCreateScreenSession,
  useGetScreenSession,
  getGetScreenSessionQueryKey,
  type ScreenSession,
} from "@workspace/api-client-react";
import { t } from "@workspace/strings/web";
import { useDevice } from "@/player/context/device-context";
import { syncActiveWorkspaceId } from "@/hooks/use-workspace";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/** Group the short pairing code into 4-char blocks so it reads across a room. */
function formatCode(code: string): string {
  return (code.match(/.{1,4}/g) ?? [code]).join(" ");
}

/**
 * Signed-out screen login. Offers two paths:
 *  - sign in directly on the screen (Clerk), which flips the entry to the
 *    on-screen device picker; or
 *  - scan the QR with an already-signed-in phone, which opens the handoff page
 *    on the phone to activate this screen. Meanwhile this screen polls its
 *    session; once activated it receives the durable credential and starts.
 */
export default function PlayerLogin({ onActivated }: { onActivated: () => void }) {
  const { adoptCredential } = useDevice();
  const createSession = useCreateScreenSession();
  const [session, setSession] = useState<ScreenSession | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const createdRef = useRef(false);

  // Create exactly one screen session for this login surface.
  useEffect(() => {
    if (createdRef.current) return;
    createdRef.current = true;
    createSession.mutate(undefined, {
      onSuccess: (s) => setSession(s),
    });
  }, [createSession]);

  // Render the activation QR once we have a session.
  useEffect(() => {
    if (!session) return;
    const url = `${window.location.origin}${basePath}/player?activate=${session.id}`;
    setQrDataUrl(qrSvgDataUrl(url));
  }, [session]);

  // Poll the session; once a phone activates it, persist the credential.
  const { data: status } = useGetScreenSession(session?.id ?? "", {
    query: {
      enabled: Boolean(session),
      refetchInterval: 2000,
      queryKey: getGetScreenSessionQueryKey(session?.id ?? ""),
    },
  });

  useEffect(() => {
    if (status?.status === "activated" && status.credentialToken) {
      adoptCredential(status.credentialToken);
      const activatedWorkspaceId = (
        status as { workspaceId?: string | null }
      ).workspaceId;
      if (activatedWorkspaceId) {
        syncActiveWorkspaceId(activatedWorkspaceId);
      }
      onActivated();
    }
  }, [status, onActivated, adoptCredential]);

  return (
    <div className="min-h-[100dvh] w-full bg-background flex items-center justify-center px-6 py-12">
      <div className="grid w-full max-w-4xl gap-8 md:grid-cols-2 items-center">
        <div className="flex flex-col items-center">
          <SignIn
            routing="hash"
            signUpUrl={`${basePath}/sign-up`}
            forceRedirectUrl={`${basePath}/player-selection`}
          />
        </div>

        <div className="flex flex-col items-center text-center rounded-2xl border bg-card p-8 shadow-sm">
          <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
            <Smartphone className="size-6" />
          </div>
          <h2 className="text-lg font-semibold tracking-tight">
            {t("playerUi.login.activateFromPhone")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xs">
            {t("playerUi.login.activateFromPhoneHint")}
          </p>
          <div className="mt-6 flex size-[220px] items-center justify-center rounded-xl border bg-white">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={t("playerUi.login.qrAlt")}
                className="size-[200px]"
                data-testid="img-activation-qr"
              />
            ) : (
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            )}
          </div>

          {session?.code ? (
            <div className="mt-6 w-full max-w-xs">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("player.pairing.orEnterCode")}
              </p>
              <p
                className="mt-1 font-mono text-3xl font-semibold tracking-[0.3em] text-foreground"
                data-testid="text-activation-code"
              >
                {formatCode(session.code)}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                {t("player.pairing.codeHint")}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
